import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { toast } from 'sonner';
import { gql } from '@apollo/client';
import { fileUtils } from '~/utils/file';
import { processFile } from '~/utils/processFile';
import { CREATE_IMAGE_MUTATION } from '~/graphql/mutations';
import { LIBRARY_IMAGES_QUERY, LIBRARY_STATS_QUERY } from '~/graphql/queries';
import { useAnalytics } from '~/context/analytics';
import { putPresignedFile, type UploadContext } from '@arthelper/ui';
import { trackUploadActivation } from '@arthelper/shared/facebook-pixel';
import { uploadOrigin } from '@arthelper/shared/upload-origin';

const GENERATE_SIGNED_URL = gql`
  mutation GenerateSignedUrl($input: SignedUrlInput!) {
    generateSignedUrl(input: $input)
  }
`;

export function useFileUpload() {
  const { amplitude } = useAnalytics();
  const [generateSignedUrl] = useMutation(GENERATE_SIGNED_URL);
  const [createImageMutation] = useMutation(CREATE_IMAGE_MUTATION, {
    // The active library pages and the count, never the whole library.
    refetchQueries: [LIBRARY_IMAGES_QUERY, LIBRARY_STATS_QUERY],
  });

  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);

  const uploadFileToS3WithProgress = async (
    fileItem: any,
    origin: ReturnType<typeof uploadOrigin>,
    ctx?: UploadContext
  ): Promise<string> => {
    const file = fileItem.file as File;
    const signal = ctx?.signal ?? new AbortController().signal;
    const isImage = file.type.startsWith('image/');
    const progress = (offset: number, weight: number) => (fraction: number) => {
      const percent = offset + fraction * weight;
      setSelectedFiles((prev) =>
        prev.map((entry) => (entry.id === fileItem.id ? { ...entry, uploadProgress: percent } : entry))
      );
      ctx?.onProgress(percent / 100);
    };
    const sign = async (type: 'public' | 'private') => {
      const { data } = await generateSignedUrl({
        variables: { input: { fileName: file.name, fileType: file.type, type } },
      });
      if (!data?.generateSignedUrl) throw new Error(`Failed to get ${type} signed URL`);
      if (signal.aborted) throw new DOMException('Upload cancelled', 'AbortError');
      return String(data.generateSignedUrl);
    };

    const publicSignedUrl = await sign('public');
    await putPresignedFile(
      file,
      { url: publicSignedUrl, contentType: file.type },
      signal,
      progress(0, isImage ? 50 : 100)
    );
    const publicUrl = publicSignedUrl.split('?')[0];
    if (!isImage) return publicUrl;

    const privateSignedUrl = await sign('private');
    await putPresignedFile(file, { url: privateSignedUrl, contentType: file.type }, signal, progress(50, 50));
    if (signal.aborted) throw new DOMException('Upload cancelled', 'AbortError');
    ctx?.onProcessing();
    const imageData = await processFile(file, fileItem.id);
    if (signal.aborted) throw new DOMException('Upload cancelled', 'AbortError');
    const { data } = await createImageMutation({
      variables: {
        input: {
          originalUrl: privateSignedUrl.split('?')[0],
          context: 'chat',
          ...origin,
          originalWidth: imageData.originalWidth,
          originalHeight: imageData.originalHeight,
          fileMeta: imageData.fileMeta,
          printMeta: imageData.printMeta,
          status: 'uploaded',
        },
      },
    });
    const created = data?.createImage;
    if (!created?.id) throw new Error('createImage returned no id');
    amplitude.track('image_uploaded', { image_id: created.id, source: 'arty_attachment' });
    trackUploadActivation(created);
    setSelectedFiles((prev) =>
      prev.map((entry) => (entry.id === fileItem.id ? { ...entry, imageId: created.id } : entry))
    );
    return publicUrl;
  };

  const handleFilesSelected = async (files: FileList | File[], ctx?: UploadContext) => {
    if (!files.length) return;

    // Capture at user gesture time; the panel can navigate while uploads run.
    const origin = uploadOrigin('arty_attachment');
    const newFiles: any[] = [];

    const processedFiles = await Promise.all(
      Array.from(files).map(async (file) => {
        const isImage = file.type.startsWith('image/');
        const isPDF = file.type === 'application/pdf';
        const isTiff = file.type === 'image/tiff' || file.type === 'image/x-tiff' || /\.tiff?$/i.test(file.name);

        if (!isImage && !isPDF) {
          toast.error(`File type not supported: ${file.name}. Only images and PDF files are allowed.`);
          if (ctx) throw new Error('unsupported-format');
          return null;
        }

        let processedFile = file;

        if (isTiff) {
          try {
            const convertedFile = await fileUtils.convertTiffToJpeg(file);
            if (convertedFile) {
              processedFile = convertedFile;
              ctx?.setPreview?.(convertedFile);
            } else {
              toast.error(`Failed to convert TIFF file: ${file.name}`);
              if (ctx) throw new Error('tiff-conversion-failed');
              return null;
            }
          } catch (error) {
            console.error('Error converting TIFF:', error);
            toast.error(`Failed to convert TIFF file: ${file.name}`);
            if (ctx) throw error;
            return null;
          }
        }

        const previewUrl = processedFile.type.startsWith('image/') ? URL.createObjectURL(processedFile) : '';
        const fileId = `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        return {
          id: fileId,
          url: previewUrl,
          file: processedFile,
          sourceFile: file,
          type: processedFile.type.startsWith('image/') ? 'image' : 'file',
          uploadProgress: 0,
          status: 'uploading',
        };
      })
    );

    const validFiles = processedFiles.filter(Boolean);
    newFiles.push(...validFiles);
    setSelectedFiles((prev) => [
      ...prev.filter((entry) => {
        const replacing = entry.status === 'error' && newFiles.some((next) => next.sourceFile === entry.sourceFile);
        if (replacing && entry.url?.startsWith('blob:')) URL.revokeObjectURL(entry.url);
        return !replacing;
      }),
      ...newFiles,
    ]);

    const uploadPromises = newFiles.map(async (fileItem) => {
      try {
        const publicUrl = await uploadFileToS3WithProgress(fileItem, origin, ctx);
        setSelectedFiles((prev) =>
          prev.map((f) => (f.id === fileItem.id ? { ...f, status: 'completed', uploadProgress: 100, publicUrl } : f))
        );
      } catch (error) {
        if (ctx?.signal.aborted) {
          removeFile(fileItem.id);
          throw error;
        }
        console.error(error);
        toast.error(`Failed to upload ${fileItem.file.name}`);
        setSelectedFiles((prev) => prev.map((f) => (f.id === fileItem.id ? { ...f, status: 'error' } : f)));
        throw error;
      }
    });

    const results = await Promise.allSettled(uploadPromises);
    if (ctx) {
      const failed = results.find((result) => result.status === 'rejected');
      if (failed?.status === 'rejected') throw failed.reason;
    }
  };

  const removeFile = (fileId: string) => {
    setSelectedFiles((prev) => {
      const fileToRemove = prev.find((f) => f.id === fileId);
      if (fileToRemove && fileToRemove.url) {
        URL.revokeObjectURL(fileToRemove.url);
      }
      return prev.filter((f) => f.id !== fileId);
    });
  };

  const clearFiles = () => {
    setSelectedFiles((prev) => {
      prev.forEach((f) => {
        if (f.url?.startsWith('blob:')) {
          URL.revokeObjectURL(f.url);
        }
      });
      return [];
    });
  };

  const restoreFiles = (files: any[]) => {
    if (!files.length) return;
    setSelectedFiles(
      files.map((file) => ({
        ...file,
        status: 'completed',
        uploadProgress: 100,
        url: file.type === 'image' && file.publicUrl ? file.publicUrl : file.url,
      }))
    );
  };

  return {
    selectedFiles,
    handleFilesSelected,
    removeFile,
    clearFiles,
    restoreFiles,
  };
}
